import React from 'react';
import { FaExclamationTriangle, FaIdCard, FaUser, FaPhone, FaEnvelope } from 'react-icons/fa';

const ProfileCompletionModal = ({
  isOpen,
  onClose,
  onCompleteProfile,
  onContinueWithoutProfile,
  userData,
  guestData,
}) => {
  if (!isOpen) return null;

  // Check what's missing
  const missingFields = {
    email: !guestData?.emailid || guestData.emailid.trim() === '',
    phone: !guestData?.phonenumber || guestData.phonenumber.toString().trim() === '',
    aadhar: !guestData?.aadhar_image_url || guestData.aadhar_image_url.trim() === '',
    profilePhoto: !guestData?.profile_photo || guestData.profile_photo.trim() === '',
  };

  console.log('Missing fields check:', missingFields);

  // Check if ONLY Aadhar is missing (all other fields are present)
  const isOnlyAadharMissing =
    missingFields.aadhar && // Aadhar is missing
    !missingFields.email && // Email is present
    !missingFields.phone && // Phone is present
    !missingFields.profilePhoto; // Profile photo is present

  // Check if Aadhar is missing along with other fields
  const isAadharWithOtherMissing =
    missingFields.aadhar &&
    (missingFields.email || missingFields.phone || missingFields.profilePhoto);

  // Check if other fields are missing (without Aadhar)
  const hasOtherMissingFieldsOnly =
    !missingFields.aadhar &&
    (missingFields.email || missingFields.phone || missingFields.profilePhoto);

  console.log('isOnlyAadharMissing:', isOnlyAadharMissing);
  console.log('isAadharWithOtherMissing:', isAadharWithOtherMissing);
  console.log('hasOtherMissingFieldsOnly:', hasOtherMissingFieldsOnly);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Modal Content */}
          <div className="bg-white p-6">
            {/* Icon - Different based on what's missing */}
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-4">
              {isOnlyAadharMissing ? (
                <div className="bg-blue-100 p-4 rounded-full">
                  <FaIdCard className="h-8 w-8 text-blue-600" />
                </div>
              ) : (
                <div className="bg-yellow-100 p-4 rounded-full">
                  <FaExclamationTriangle className="h-8 w-8 text-yellow-600" />
                </div>
              )}
            </div>

            {/* Title */}
            <h3 className="text-center text-2xl font-bold text-gray-900 mb-4 font-poppins">
              {isOnlyAadharMissing ? 'Aadhar Card Verification Required' : 'Incomplete Profile'}
            </h3>

            {/* Message */}
            <div className="text-center mb-6">
              <p className="text-gray-600 font-poppins mb-2">
                Hi {guestData?.firstname || 'Guest'},
              </p>

              {isOnlyAadharMissing ? (
                // Case 1: ONLY Aadhar is missing
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                  <p className="text-blue-800 font-poppins text-sm mb-3">
                    Your profile is complete except for Aadhar card verification.
                  </p>
                  <div className="flex items-start gap-3">
                    <FaIdCard className="text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-700 font-medium">Aadhar Card Not Uploaded</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Aadhar card can only be uploaded and verified by our front desk team.
                      </p>
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs text-yellow-800">
                          <span className="font-semibold">📋 Next Steps:</span>
                          <br />
                          1. Visit the front desk with your physical Aadhar card
                          <br />
                          2. Our team will upload and verify your Aadhar
                          <br />
                          3. After verification, you can proceed with check-in
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isAadharWithOtherMissing ? (
                // Case 2: Aadhar + other fields missing
                <div className="space-y-3">
                  <p className="text-gray-600 font-poppins">
                    Your profile is incomplete. Please complete the following fields:
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-left">
                    {/* Show missing fields */}
                    {missingFields.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <FaEnvelope className="text-red-500" />
                        <span>Email address is missing</span>
                      </div>
                    )}
                    {missingFields.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <FaPhone className="text-red-500" />
                        <span>Phone number is missing</span>
                      </div>
                    )}
                    {missingFields.profilePhoto && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <FaUser className="text-red-500" />
                        <span>Profile photo is missing</span>
                      </div>
                    )}
                    {missingFields.aadhar && (
                      <div className="flex items-start gap-2 text-sm text-gray-700">
                        <FaIdCard className="text-red-500 mt-0.5" />
                        <div>
                          <span>Aadhar card is not uploaded</span>
                          <p className="text-xs text-gray-500 mt-1">
                            Aadhar card can only be uploaded by front desk team
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : hasOtherMissingFieldsOnly ? (
                // Case 3: Only other fields missing (no Aadhar)
                <div>
                  <p className="text-gray-600 font-poppins">
                    Your profile is incomplete. Please complete your profile to continue with the
                    check-in process.
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 mt-3 text-left">
                    <p className="text-sm font-medium text-gray-700 mb-2">Missing Information:</p>
                    <div className="space-y-1">
                      {missingFields.email && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaEnvelope className="text-red-400" />
                          <span>Email address</span>
                        </div>
                      )}
                      {missingFields.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaPhone className="text-red-400" />
                          <span>Phone number</span>
                        </div>
                      )}
                      {missingFields.profilePhoto && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaUser className="text-red-400" />
                          <span>Profile photo</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Fallback: All fields are present (should not happen)
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 text-sm">
                    ✅ Your profile is complete! You can proceed with check-in.
                  </p>
                </div>
              )}
            </div>

            {/* Buttons - Based on scenario */}
            <div className="flex flex-col sm:flex-row gap-3">
              {isOnlyAadharMissing ? (
                // ONLY Aadhar missing - Show message with close button
                <button
                  onClick={onClose}
                  className="w-full bg-gray-600 text-white hover:bg-gray-700 font-poppins font-semibold py-3 px-4 rounded-lg transition duration-200"
                >
                  OK, I Understand
                </button>
              ) : isAadharWithOtherMissing ? (
                // Aadhar + other fields missing - Show Complete Profile button only
                <button
                  onClick={onCompleteProfile}
                  className="w-full bg-black text-white hover:bg-gray-800 font-poppins font-semibold py-3 px-4 rounded-lg transition duration-200"
                >
                  Complete Profile
                </button>
              ) : hasOtherMissingFieldsOnly ? (
                // Only other fields missing (no Aadhar) - Show Complete Profile button only
                <button
                  onClick={onCompleteProfile}
                  className="w-full bg-black text-white hover:bg-gray-800 font-poppins font-semibold py-3 px-4 rounded-lg transition duration-200"
                >
                  Complete Profile
                </button>
              ) : (
                // All complete - Show Proceed button
                <button
                  onClick={onClose}
                  className="w-full bg-green-600 text-white hover:bg-green-700 font-poppins font-semibold py-3 px-4 rounded-lg transition duration-200"
                >
                  Proceed to Check-In
                </button>
              )}
            </div>

            {/* Close button (X) */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
